import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect } from 'react';
import { Plus, Trash2, Check, ExternalLink, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyProfiles = ({ onSwitch }) => {
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { fetchProfiles(); }, []);

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API}/profiles/list`);
      setProfiles(res.data.profiles);
      setActiveId(res.data.active_profile_id);
    } catch (err) { toast.error('Failed to load profiles'); }
  };

  const handleCreate = async () => {
    if (!newUsername.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/profiles/create`, {
        username: newUsername.trim(),
        display_name: newDisplayName.trim() || undefined,
      });
      toast.success('Profile created');
      setShowCreate(false);
      setNewUsername('');
      setNewDisplayName('');
      fetchProfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally { setCreating(false); }
  };

  const handleSwitch = async (profileId) => {
    if (profileId === activeId) return;
    try {
      await axios.post(`${API}/profiles/${profileId}/switch`);
      setActiveId(profileId);
      toast.success('Switched profile');
      if (onSwitch) onSwitch();
    } catch (err) { toast.error('Failed to switch'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/profiles/${deleteTarget}`);
      toast.success('Profile deleted');
      setDeleteTarget(null);
      fetchProfiles();
      if (deleteTarget === activeId && onSwitch) onSwitch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="max-w-2xl" data-testid="my-profiles-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">My Profiles</h1>
          <p className="text-[#A1A1AA] mt-1">Manage multiple business profiles under one account</p>
        </div>
        <Button data-testid="create-profile-btn" onClick={() => setShowCreate(true)}
          className="rounded-full bg-violet-500 hover:bg-violet-600 text-white gap-1.5">
          <Plus className="w-4 h-4" /> New Profile
        </Button>
      </div>

      <div className="space-y-3">
        {profiles.map(p => (
          <div key={p.id} data-testid={`profile-card-${p.username}`}
            className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
              p.is_active
                ? 'bg-violet-500/5 border-violet-500/30'
                : 'bg-[#121212] border-[#27272A] hover:border-[#3F3F46]'
            }`}>
            {p.avatar_url ? (
              <img src={resolveMediaUrl(p.avatar_url)}
                alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#1E1E1E] flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-[#52525B]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white truncate">{p.display_name || p.username}</h3>
                {p.is_active && (
                  <span className="text-[9px] bg-violet-500/20 text-violet-400 px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide">Active</span>
                )}
              </div>
              <p className="text-sm text-[#71717A] truncate">createonelink.com/{p.username}</p>
              {p.business_name && <p className="text-xs text-[#52525B] mt-0.5">{p.business_name}</p>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a href={`/${p.username}`} target="_blank" rel="noopener noreferrer"
                className="p-2 rounded-lg text-[#71717A] hover:text-white hover:bg-white/5 transition-all" title="View public profile">
                <ExternalLink className="w-4 h-4" />
              </a>
              {!p.is_active && (
                <Button data-testid={`switch-to-${p.username}`} onClick={() => handleSwitch(p.id)} size="sm" variant="outline"
                  className="rounded-full border-[#27272A] text-white hover:bg-violet-500/10 hover:border-violet-500/30 text-xs gap-1">
                  <Check className="w-3 h-3" /> Switch
                </Button>
              )}
              {p.id !== activeId && profiles.length > 1 && profiles[0]?.id !== p.id && (
                <button data-testid={`delete-profile-${p.username}`} onClick={() => setDeleteTarget(p.id)}
                  className="p-2 rounded-lg text-[#52525B] hover:text-red-400 hover:bg-red-500/10 transition-all">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Profile Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Profile</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Add a new business profile. Each profile gets its own public page, links, and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[#EDEDED]">Username</Label>
              <Input data-testid="new-profile-username" value={newUsername}
                onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                className="mt-2 bg-[#121212] border-[#27272A] text-white" placeholder="e.g. mortgage_biz" autoFocus />
              {newUsername && <p className="text-xs text-[#71717A] mt-1">createonelink.com/{newUsername}</p>}
            </div>
            <div>
              <Label className="text-[#EDEDED]">Display Name (optional)</Label>
              <Input data-testid="new-profile-display-name" value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                className="mt-2 bg-[#121212] border-[#27272A] text-white" placeholder="e.g. My Mortgage Business" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-[#A1A1AA]">Cancel</Button>
            <Button data-testid="confirm-create-profile" onClick={handleCreate} disabled={creating || !newUsername.trim() || newUsername.trim().length < 3}
              className="rounded-full bg-violet-500 hover:bg-violet-600 text-white">
              {creating ? 'Creating...' : 'Create Profile'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Profile?</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              This will permanently delete this profile and all its data (links, products, events, etc.). This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} className="text-[#A1A1AA]">Cancel</Button>
            <Button data-testid="confirm-delete-profile" onClick={handleDelete}
              className="rounded-full bg-red-500 hover:bg-red-600 text-white">Delete Profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyProfiles;
